import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { Check as CheckIcon, Receipt as ReceiptIcon, Download as DownloadIcon } from '@mui/icons-material';
import { Plus, Minus, Trash } from '@phosphor-icons/react';
import api from '../../utils/api';
import { formatGBP, formatGBPFromPounds } from '../../utils/currency';
import { getPriceSuffix } from '../../utils/priceDisplay';
import { useAuth } from '../../context/AuthContext';
import useThemeStyles from '../../theme/useThemeStyles';
import PricingCard from './PricingCard';

function Billing() {
  const styles = useThemeStyles();
  const { _user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [_paymentHistory, setPaymentHistory] = useState([]);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [changePlanDialog, setChangePlanDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [_billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [aiAddon, setAiAddon] = useState(null);
  const [aiAddonLoading, setAiAddonLoading] = useState(true);
  const [addonUpdating, setAddonUpdating] = useState(false);
  const [removeAddonDialog, setRemoveAddonDialog] = useState(false);

  const fetchAiMessagesAddon = async () => {
    try {
      setAiAddonLoading(true);
      const response = await api.get('/api/billing/ai-messages-addon');
      setAiAddon(response.data);
    } catch (e) {
      setAiAddon(null);
    } finally {
      setAiAddonLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchSubscriptionDetails();
    fetchMembershipStatus();
    fetchBillingInfo();
    
    if (searchParams.get('success')) {
      setSuccess('Thank you for your subscription! Your account has been upgraded.');
      // Webhooks often do not reach localhost; sync from Stripe so the plan shows without Stripe CLI.
      (async () => {
        try {
          await api.post('/api/billing/reconcile-stripe-subscription');
        } catch (e) {
          // Non-fatal; user can refresh or rely on webhooks in production
        }
        await fetchSubscriptionDetails();
        await fetchMembershipStatus();
        await fetchAiMessagesAddon();
      })();
    } else if (searchParams.get('canceled')) {
      setError('Subscription process was cancelled.');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchAiMessagesAddon();
  }, [subscription?.subscriptionId, subscription?.membershipLevel, subscription?.status]);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/api/billing/plans');
      // API returns an array, ensure it's an array
      const plansData = Array.isArray(response.data) ? response.data : [];
      const allowed = new Set(['free', 'complete']);
      setPlans(plansData.filter((p) => allowed.has(p.key)));
    } catch (error) {
      setError('Failed to fetch plans');
      setPlans([]); // Set to empty array on error
    }
  };

  const fetchSubscriptionDetails = async () => {
    try {
      const response = await api.get('/api/billing/subscription');
      setSubscription(response.data);
      setPaymentHistory(response.data.payments || []);
    } catch (error) {
      // 404 is expected if user has no subscription - not an error
      if (error.response?.status === 404) {
        setSubscription({ status: 'free', membershipLevel: 'free' });
        setPaymentHistory([]);
      } else {}
    }
  };

  const fetchMembershipStatus = async () => {
    try {
      const response = await api.get('/api/billing/status');
      setMembershipStatus(response.data);
    } catch (error) {
      // 404 is acceptable - user might not have a wedding yet
      if (error.response?.status === 404) {
        setMembershipStatus({ membershipLevel: 'free' });
      } else {}
    }
  };

  const fetchBillingInfo = async () => {
    try {
      setLoading(true);
      const [billingRes, invoicesRes] = await Promise.allSettled([
        api.get('/api/billing'),
        api.get('/api/billing/invoices')
      ]);
      
      // Handle billing response
      if (billingRes.status === 'fulfilled') {
        setBilling(billingRes.value.data);
      } else if (billingRes.reason?.response?.status !== 404) {}
      
      // Handle invoices response
      if (invoicesRes.status === 'fulfilled') {
        setInvoices(invoicesRes.value.data);
      } else if (invoicesRes.reason?.response?.status !== 404) {
        setInvoices([]);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      // Don't set error state for 404s - they're expected
      if (error.response?.status !== 404) {
        setError('Failed to load billing information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    // Ensure plan has required properties
    const planData = {
      ...plan,
      key: plan.key || plan.id || plan.name?.toLowerCase().replace(/\s+/g, '-'),
      id: plan.id || plan.stripePriceId || plan.key,
      name: plan.displayName || plan.name,
      price: plan.price || 0
    };
    setSelectedPlan(planData);
    setChangePlanDialog(true);
  };

  const handleSubscriptionAction = async () => {
    try {
      setLoading(true);
      setChangePlanDialog(false);

      if (selectedPlan?.key === 'free') {
        if (subscription?.subscriptionId) {
          await api.post('/api/billing/cancel-subscription');
          setSuccess(
            'Your subscription will be cancelled at the end of your billing period. You will keep access until then.'
          );
          await fetchSubscriptionDetails();
          await fetchMembershipStatus();
          await fetchAiMessagesAddon();
          return;
        }
        // No Stripe subscription (e.g. manual Complete / missed webhooks): set Free in the app
        await api.post('/api/billing/update-subscription', {
          membershipLevelKey: 'free'
        });
        setSuccess('Your plan is now Free.');
        await fetchSubscriptionDetails();
        await fetchMembershipStatus();
        await fetchAiMessagesAddon();
        return;
      }

      if (!subscription?.subscriptionId) {
        // New subscription (Complete only; Free does not use checkout)
        const membershipLevelKey = selectedPlan.key || selectedPlan.id;
        
        const response = await api.post('/api/billing/create-checkout-session', {
          membershipLevelKey: membershipLevelKey,
          billingCycle: 'monthly'
        });
        
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error('No checkout URL returned from server');
        }
      } else {
        // Update existing subscription
        const _response = await api.post('/api/billing/update-subscription', {
          newPriceId: selectedPlan.stripePriceId || selectedPlan.id,
          membershipLevelKey: selectedPlan.key
        });
        setSuccess(`Your subscription has been updated to ${selectedPlan.name}`);
        fetchSubscriptionDetails();
        fetchAiMessagesAddon();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to process subscription. Please try again.';
      setError(errorMessage);
      setChangePlanDialog(true); // Reopen dialog to show error
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      setConfirmDialog(false); // Close the dialog
      const response = await api.post('/api/billing/cancel-subscription');
      setSuccess(response.data?.message || 'Subscription will be cancelled at the end of your billing period');
      fetchSubscriptionDetails();
      fetchMembershipStatus();
      fetchAiMessagesAddon();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to cancel subscription';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/billing/reactivate-subscription');
      setSuccess(response.data?.message || 'Your subscription has been reactivated.');
      // Refresh subscription details after a short delay to allow backend to update
      setTimeout(() => {
        fetchSubscriptionDetails();
        fetchMembershipStatus();
        fetchAiMessagesAddon();
      }, 500);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to reactivate subscription. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return formatGBP(amount);
  };

  const handleSetAiAddonQuantity = async (nextQuantity) => {
    if (!aiAddon?.canManage || addonUpdating) return;
    const max = aiAddon.maxQuantity || 10;
    const q = Math.max(0, Math.min(max, nextQuantity));
    try {
      setAddonUpdating(true);
      setError('');
      if (q === 0) {
        await api.delete('/api/billing/ai-messages-addon');
        setSuccess('Add-on will be cancelled at the end of your billing period.');
      } else {
        await api.post('/api/billing/ai-messages-addon', { quantity: q });
        setSuccess('Add-on updated.');
      }
      await fetchAiMessagesAddon();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update add-on');
    } finally {
      setAddonUpdating(false);
      setRemoveAddonDialog(false);
    }
  };

  const handleReactivateAiAddon = async () => {
    if (!aiAddon?.canManage || addonUpdating) return;
    try {
      setAddonUpdating(true);
      setError('');
      await api.post('/api/billing/ai-messages-addon/reactivate');
      setSuccess('Add-on reactivated.');
      await fetchAiMessagesAddon();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not reactivate add-on');
    } finally {
      setAddonUpdating(false);
    }
  };

  const renderPlanCards = () => {
    if (!plans || !Array.isArray(plans) || plans.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading plans...
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {plans.map((plan) => {
            // Check if current plan matches (by key or name)
            const isCurrentPlan = subscription && (
              subscription.membershipLevel === plan.key || 
              subscription.membershipLevel === plan.name?.toLowerCase().split(' ')[0]
            );

            const showCancelOnCard =
              isCurrentPlan &&
              plan.key === 'complete' &&
              Boolean(subscription?.subscriptionId) &&
              subscription?.status !== 'free' &&
              !subscription?.cancelAtPeriodEnd;

            return (
              <Grid item xs={12} sm={6} key={plan.key || plan.id || plan.name}>
                <PricingCard
                  plan={plan}
                  billingCycle="monthly"
                  isCurrentPlan={isCurrentPlan}
                  isCancelling={Boolean(subscription?.cancelAtPeriodEnd) && isCurrentPlan}
                  onSelect={handleSelectPlan}
                  onReactivate={handleReactivateSubscription}
                  onCancelRequest={() => setConfirmDialog(true)}
                  showCancelSubscription={showCancelOnCard}
                  loading={loading}
                />
              </Grid>
            );
          })}
      </Grid>
    );
  };

  const _downloadInvoice = async (invoiceId) => {
    try {
      const response = await api.get(`/api/billing/invoices/${invoiceId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Failed to download invoice');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, mb: 3 }}>
        Billing
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading && !subscription ? (
        <Box sx={styles.patterns.loadingState}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Plans &amp; Pricing
            </Typography>
            {renderPlanCards()}
            {subscription?.cancelAtPeriodEnd && subscription?.currentPeriodEnd && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Your Complete plan is scheduled to be cancelled on {formatDate(subscription.currentPeriodEnd)}.
              </Alert>
            )}
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Add-ons
            </Typography>
            {aiAddonLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
              </Box>
            ) : aiAddon ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={4}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid',
                      borderColor: aiAddon.isActive ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      position: 'relative',
                    }}
                  >
                    {aiAddon.isActive && (
                      <Chip
                        label={aiAddon.isCancelled ? 'Cancelling' : 'Active'}
                        color={aiAddon.isCancelled ? 'warning' : 'primary'}
                        size="small"
                        sx={{ position: 'absolute', top: 16, right: 16 }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1, pt: aiAddon.isActive ? 6 : 2 }}>
                      <Typography variant="h6" gutterBottom>
                        {aiAddon.displayName}
                      </Typography>
                      {aiAddon.price != null && aiAddon.price !== undefined ? (
                        <Typography variant="h4" color="primary" gutterBottom>
                          {formatGBPFromPounds(
                            aiAddon.isQuantityBased && aiAddon.quantity > 1
                              ? aiAddon.price * aiAddon.quantity
                              : aiAddon.price
                          )}
                          {aiAddon.isQuantityBased && aiAddon.quantity > 1 && (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {' '}
                              ({aiAddon.quantity} × {formatGBPFromPounds(aiAddon.price)})
                            </Typography>
                          )}
                          <Typography component="span" variant="body2" color="text.secondary">
                            {getPriceSuffix(aiAddon.price, 'month')}
                          </Typography>
                        </Typography>
                      ) : null}
                      {aiAddon.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {aiAddon.description}
                        </Typography>
                      )}
                      {aiAddon.messagesPerUnit != null && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {aiAddon.messagesPerUnit.toLocaleString()} AI messages per unit per month, shared across your
                          wedding.
                        </Typography>
                      )}
                      {aiAddon.unavailableReason && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          {aiAddon.unavailableReason}
                        </Alert>
                      )}
                    </CardContent>
                    <Box sx={{ p: 2, pt: 0 }}>
                      {!aiAddon.isActive ? (
                        <Button
                          variant="contained"
                          fullWidth
                          disabled={!aiAddon.canManage || addonUpdating}
                          onClick={() => handleSetAiAddonQuantity(1)}
                        >
                          Add add-on
                        </Button>
                      ) : aiAddon.canManage ? (
                        <Stack spacing={1}>
                          {aiAddon.isCancelled ? (
                            <>
                              <Alert severity="info">
                                This add-on is scheduled to be cancelled on{' '}
                                {aiAddon.scheduledDeletionDate
                                  ? new Date(aiAddon.scheduledDeletionDate).toLocaleDateString()
                                  : 'your renewal date'}
                                .
                              </Alert>
                              <Button
                                variant="contained"
                                fullWidth
                                disabled={addonUpdating}
                                onClick={handleReactivateAiAddon}
                              >
                                Reactivate add-on
                              </Button>
                            </>
                          ) : (
                            <>
                              {aiAddon.isQuantityBased && aiAddon.maxQuantity && (
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                              <Tooltip title="Decrease quantity">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleSetAiAddonQuantity(
                                        Math.max(0, (aiAddon.quantity || 1) - 1)
                                      )
                                    }
                                    disabled={addonUpdating || (aiAddon.quantity || 1) <= 0}
                                    aria-label="Decrease quantity"
                                  >
                                    <Minus size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Typography variant="body2" fontWeight={500} sx={{ minWidth: 24, textAlign: 'center' }}>
                                {aiAddon.quantity || 1}
                              </Typography>
                              <Tooltip title="Increase quantity">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleSetAiAddonQuantity(
                                        Math.min(
                                          aiAddon.maxQuantity || 10,
                                          (aiAddon.quantity || 1) + 1
                                        )
                                      )
                                    }
                                    disabled={
                                      addonUpdating ||
                                      (aiAddon.quantity || 1) >= (aiAddon.maxQuantity || 10)
                                    }
                                    aria-label="Increase quantity"
                                  >
                                    <Plus size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {addonUpdating && <CircularProgress size={16} sx={{ ml: 0.5 }} />}
                            </Box>
                              )}
                              <Button
                                variant="outlined"
                                color="error"
                                fullWidth
                                disabled={addonUpdating}
                                startIcon={<Trash size={16} />}
                                onClick={() => setRemoveAddonDialog(true)}
                              >
                                Remove add-on
                              </Button>
                            </>
                          )}
                        </Stack>
                      ) : null}
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Alert severity="info">Add-on information could not be loaded.</Alert>
            )}
          </Box>

          <Dialog open={removeAddonDialog} onClose={() => !addonUpdating && setRemoveAddonDialog(false)}>
            <DialogTitle>Remove add-on</DialogTitle>
            <DialogContent>
              <Typography>
                This add-on will be available until the end of your current billing period, then cancelled
                automatically.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRemoveAddonDialog(false)} disabled={addonUpdating}>
                Cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                disabled={addonUpdating}
                onClick={() => handleSetAiAddonQuantity(0)}
              >
                Remove
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to cancel your subscription? You will still have access until the end of your current billing period.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDialog(false)}>No, Keep Subscription</Button>
              <Button onClick={handleCancelSubscription} color="error">
                Yes, Cancel Subscription
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={changePlanDialog} onClose={() => setChangePlanDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              {subscription?.subscriptionId ? 'Change Subscription Plan' : 'Confirm Subscription'}
            </DialogTitle>
            <DialogContent>
              {selectedPlan && (
                <>
                  <Typography variant="h6" gutterBottom>
                    {selectedPlan.name || selectedPlan.displayName}
                  </Typography>
                  
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'background.default', 
                    borderRadius: 2,
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Monthly price (inc. VAT)
                    </Typography>
                    <Typography variant="h5" fontWeight={700} color="primary">
                      {selectedPlan.price === 0 
                        ? 'Free' 
                        : `£${selectedPlan.price.toFixed(2)}/month`}
                    </Typography>
                  </Box>
                  {selectedPlan.description && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {selectedPlan.description}
                    </Typography>
                  )}
                  {selectedPlan.features && Array.isArray(selectedPlan.features) && selectedPlan.features.length > 0 ? (
                    <>
                      <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                        Features:
                      </Typography>
                      <List dense>
                        {selectedPlan.features.map((feature, index) => (
                          <ListItem key={index} disableGutters>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <CheckIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={typeof feature === 'string' ? feature : feature.name || feature} />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                      No features listed for this plan
                    </Typography>
                  )}
                  {(selectedPlan.price === 0 || selectedPlan.key === 'free') && subscription?.status !== 'free' && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        Downgrading to the free plan will limit your access to premium features.
                      </Typography>
                    </Alert>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setChangePlanDialog(false)}>Cancel</Button>
              <Button onClick={handleSubscriptionAction} color="primary" disabled={loading}>
                {selectedPlan?.key === 'free' || selectedPlan?.price === 0
                  ? 'Downgrade'
                  : subscription?.subscriptionId
                    ? 'Change Plan'
                    : 'Subscribe'}
              </Button>
            </DialogActions>
          </Dialog>

          {(invoices.length > 0 || (membershipStatus?.paymentHistory && membershipStatus.paymentHistory.length > 0)) && (
            <Card sx={{ ...styles.card.default, mt: 4 }}>
              <CardContent>
                <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                  <ReceiptIcon color="primary" />
                  <Typography variant="h6" sx={styles.text.sectionTitle}>
                    Billing History
                  </Typography>
                </Box>
                
                {(invoices.length === 0 && (!membershipStatus?.paymentHistory || membershipStatus.paymentHistory.length === 0)) ? (
                  <Typography variant="body2" color="text.secondary">
                    No billing history available
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Use invoices if available, otherwise fall back to paymentHistory */}
                        {(invoices.length > 0 ? invoices : (membershipStatus?.paymentHistory || [])).map((item) => {
                          const invoice = invoices.length > 0 ? item : null;
                          const payment = invoices.length === 0 ? item : null;
                          const displayItem = invoice || payment;
                          
                          return (
                            <TableRow key={displayItem.id}>
                              <TableCell>
                                {formatDate(invoice ? invoice.date : payment.date)}
                              </TableCell>
                              <TableCell>
                                {invoice && invoice.periodStart && invoice.periodEnd 
                                  ? `Subscription ${formatDate(invoice.periodStart)} - ${formatDate(invoice.periodEnd)}`
                                  : 'Subscription Payment'
                                }
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight={500}>
                                    {formatCurrency(displayItem.netAmount !== undefined ? displayItem.netAmount : displayItem.amount)} {displayItem.currency?.toUpperCase() || 'GBP'}
                                  </Typography>
                                  {displayItem.refundAmount > 0 && (
                                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                      Refunded: {formatCurrency(displayItem.refundAmount)} {displayItem.currency?.toUpperCase() || 'GBP'}
                                    </Typography>
                                  )}
                                  {displayItem.refundAmount > 0 && displayItem.amount && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textDecoration: 'line-through', opacity: 0.6 }}>
                                      Original: {formatCurrency(displayItem.amount)} {displayItem.currency?.toUpperCase() || 'GBP'}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={displayItem.status?.charAt(0).toUpperCase() + displayItem.status?.slice(1)} 
                                  size="small"
                                  color={displayItem.status === 'paid' ? 'success' : 'warning'}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {(invoice?.hostedInvoiceUrl || payment?.receiptUrl) && (
                                  <Button
                                    size="small"
                                    href={invoice?.hostedInvoiceUrl || payment?.receiptUrl}
                                    target="_blank"
                                    startIcon={<DownloadIcon />}
                                    sx={{ mr: 1 }}
                                    variant={(invoice?.status || payment?.status) === 'paid' ? 'outlined' : 'contained'}
                                    color="primary"
                                  >
                                    {(invoice?.status || payment?.status) === 'paid' ? 'View Invoice' : 'Pay Online'}
                                  </Button>
                                )}
                                {invoice?.invoicePdf && (
                                  <Button
                                    size="small"
                                    href={invoice.invoicePdf}
                                    target="_blank"
                                    startIcon={<DownloadIcon />}
                                  >
                                    Download PDF
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Container>
  );
}

export default Billing; 